/* eslint-disable*/
import { sentenceCase } from 'change-case';
import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardHeader,
    Divider,
    IconButton,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
// utils
import { useNavigate } from 'react-router-dom';
import { fCurrency } from 'src/utils/formatNumber';
import Iconify from '../../../components/iconify/Iconify';
import Label from '../../../components/label/Label';
import Scrollbar from '../../../components/scrollbar/Scrollbar';

// ----------------------------------------------------------------------

RecentLoans.propTypes = {
  recentAssets: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
};

const LoadingSkeleton = () => (
  <Card>
    <CardHeader title="Recent Activity" sx={{ mb: 3 }} />
    <Box sx={{ p: 2 }}>
      {[...Array(5)].map((_, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={40} />
        </Box>
      ))}
    </Box>
  </Card>
);

const EmptyState = () => (
  <Card>
    <CardHeader title="Recent Activity" sx={{ mb: 3 }} />
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Iconify icon="mdi:clipboard-text-outline" width={48} height={48} sx={{ color: 'text.disabled', mb: 2 }} />
      <Typography variant="body2" color="text.secondary">
        No recent activity found
      </Typography>
    </Box>
  </Card>
);

export default function RecentLoans({ recentAssets, isLoading, error, onRefresh }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader
          title="Recent Activity"
          action={
            onRefresh && (
              <IconButton onClick={onRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            )
          }
          sx={{ mb: 3 }}
        />
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      </Card>
    );
  }

  if (!recentAssets || recentAssets?.length === 0) {
    return <EmptyState />;
  }

  return (
    <Card>
      <CardHeader
        title="Recent Activity"
        action={
          onRefresh && (
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          )
        }
        sx={{ mb: 3 }}
      />
      <Scrollbar>
        <TableContainer sx={{ minWidth: 720 }} component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Asset ID</TableCell>
                <TableCell>Asset Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Barcode</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Purchase Cost</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {recentAssets?.map((asset, index) => (
                  
                <TableRow
                  key={asset.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">{asset.asset_tag || 'N/A'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          mr: 2,
                          bgcolor: (theme) => theme.palette.primary.lighter,
                          color: (theme) => theme.palette.primary.main,
                        }}
                      >
                        <Iconify icon="material-symbols:inventory-2-outline" width={16} height={16} />
                      </Avatar>
                      <Typography variant="subtitle2">{asset.name || 'N/A'}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{asset.category?.name || 'N/A'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{asset.barcode_number || 'N/A'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{asset.serial_number || 'N/A'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{fCurrency(asset.purchase_cost)}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variantstatus="body2">{asset.location?.name || 'N/A'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{asset.custodian?.display_name || 'Unassigned'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Label
                      color={
                        (asset?.status === 'active' && 'success') ||
                        (asset?.status === 'in_store' && 'warning') ||
                        (asset?.status === 'retired' && 'default') ||
                        (asset?.status === 'disposed' && 'error') ||
                        'default'
                      }
                      variant={isLight ? 'soft' : 'filled'}
                    >
                      {asset?.status && sentenceCase(asset?.status)}
                    </Label>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Divider />

      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Iconify icon="eva:eye-fill" />}
          onClick={() => navigate('/dashboard/assets')}
        >
          View All Assets
        </Button>
      </Box>
    </Card>
  );
}

// ----------------------------------------------------------------------

AvatarIcon.propTypes = {
  icon: PropTypes.string.isRequired,
};

function AvatarIcon({ icon }) {
  return (
    <Avatar
      sx={{
        width: 48,
        height: 48,
        color: 'text.secondary',
        bgcolor: 'background.neutral',
      }}
    >
      <Iconify icon={icon} width={24} height={24} />
    </Avatar>
  );
}

// ----------------------------------------------------------------------

function MoreMenuButton() {
  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const ICON = {
    mr: 2,
    width: 20,
    height: 20,
  };
}
